/** URL query param for the Date listed filter (coarse token, not an ISO timestamp). */
export const LISTED_PARAM = 'listed';

/** Default / cleared selection — omit from the URL. */
export const LISTED_ANY = 'any';

export const LISTED_TOKENS = ['24h', '3d', '7d', '14d', '30d'];

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

/** Rolling window length in ms for each non-default token. */
export const LISTED_WINDOW_MS = {
  '24h': 24 * MS_HOUR,
  '3d': 3 * MS_DAY,
  '7d': 7 * MS_DAY,
  '14d': 14 * MS_DAY,
  '30d': 30 * MS_DAY,
};

export const LISTED_OPTIONS = [
  { token: LISTED_ANY, labelId: 'DateListedFilter.optionAny', hintId: 'DateListedFilter.hintAny' },
  { token: '24h', labelId: 'DateListedFilter.option24h', hintId: 'DateListedFilter.hint24h' },
  { token: '3d', labelId: 'DateListedFilter.option3d', hintId: 'DateListedFilter.hint3d' },
  { token: '7d', labelId: 'DateListedFilter.option7d', hintId: 'DateListedFilter.hint7d' },
  { token: '14d', labelId: 'DateListedFilter.option14d', hintId: 'DateListedFilter.hint14d' },
  { token: '30d', labelId: 'DateListedFilter.option30d', hintId: 'DateListedFilter.hint30d' },
];

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export const isListedToken = value => LISTED_TOKENS.includes(value);

/**
 * Normalize a URL/query value to a known token, or `any` when absent/invalid.
 * @param {unknown} value
 * @returns {string}
 */
export const normalizeListedToken = value => {
  if (value == null || value === '' || value === LISTED_ANY) {
    return LISTED_ANY;
  }
  return isListedToken(value) ? value : LISTED_ANY;
};

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export const isListedFilterActive = value => isListedToken(value);

/**
 * Rolling-window lower bound for Sharetribe `createdAtStart`.
 * Kept for the Integration /api/search-listings sanitizer.
 * @param {string} token
 * @param {number} [nowMs]
 * @returns {string|null} ISO 8601 timestamp, or null when no filter applies
 */
export const listedTokenToCreatedAtStart = (token, nowMs = Date.now()) => {
  const windowMs = LISTED_WINDOW_MS[token];
  if (windowMs == null) {
    return null;
  }
  return new Date(nowMs - windowMs).toISOString();
};

/**
 * Rolling-window lower bound for `pub_publishedAt` (unix ms).
 * @param {string} token
 * @param {number} [nowMs]
 * @returns {number|null}
 */
export const listedTokenToPublishedAtMin = (token, nowMs = Date.now()) => {
  const windowMs = LISTED_WINDOW_MS[token];
  if (windowMs == null) {
    return null;
  }
  return nowMs - windowMs;
};

/**
 * Map URL `listed` onto Marketplace API params.
 * - Always drop client-supplied createdAtStart/createdAtEnd (URL forgery).
 * - When a valid token is set: add pub_publishedAt as an inclusive min range (`min,`).
 * - Do not send `listed` to the API; it is URL state only.
 *
 * @param {Object} params
 * @param {number} [nowMs]
 * @returns {Object}
 */
export const applyListedFilterToSearchParams = (params = {}, nowMs = Date.now()) => {
  const {
    [LISTED_PARAM]: listedRaw,
    createdAtStart: _ignoredStart,
    createdAtEnd: _ignoredEnd,
    ...rest
  } = params;
  const token = normalizeListedToken(listedRaw);
  const publishedAtMin = listedTokenToPublishedAtMin(token, nowMs);
  if (publishedAtMin == null) {
    return rest;
  }
  return { ...rest, pub_publishedAt: `${publishedAtMin},` };
};

export const toUrlParams = token => ({
  [LISTED_PARAM]: token && token !== LISTED_ANY && isListedToken(token) ? token : null,
});

export const clearUrlParams = () => ({
  [LISTED_PARAM]: null,
});
